import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Observable, of } from 'rxjs';

//Get the base url from the environment file.
import { environment } from '../../environments/environment';
import { LooseObject } from '../objects/loose-object';
import { isBoolean, isNumber, isObject, merge, round } from 'lodash-es';
import { TranslateService } from '@ngx-translate/core';
import { blank, data_get, fallback, filled, transform } from '../shared/utils/common';
import { AdvanceReportResponse, Aggregation, AssetTypeAttributeResponse, CategoryMapping, ChartConfig, DashboardDataResponse } from '../objects/advance-reports';
import { DashboardType } from '../dashboard-new/dashboard-new.interface';
import { spf } from '../shared/utils/str';
import { AdvanceSearchboxTemplate } from '../objects/advance-searchbox';
import { AdvanceSearchboxService } from './advance-searchbox.service';
import { map } from 'rxjs/operators';

const kBaseUrl: string = environment.url + "/advance_reports/";

@Injectable()
export class AdvanceReportService {
  constructor(
    private http: HttpClient,
    private translate: TranslateService,
    private advanceSearchboxService: AdvanceSearchboxService,
  ) { }

  searchAssetTypes(searchTerm: string = '', page: number = 1): Observable<AssetTypeAttributeResponse[]> {
    let remoteUrl = `${kBaseUrl}asset_types?term=${searchTerm}&page=${page}`;
    let body = new URLSearchParams();
    return this.http.post<AssetTypeAttributeResponse[]>(remoteUrl, body.toString());
  }

  getAssetTypeAttribute(id: string): Observable<AssetTypeAttributeResponse|null> {
    if (filled(id) === false) {
      return of(null);
    }
    let remoteUrl = `${kBaseUrl}asset_type_attributes/${id}`;
    let body = new URLSearchParams();
    return this.http.post<AssetTypeAttributeResponse>(remoteUrl, body.toString());
  }

  listAdvanceReports(module: string): Observable<LooseObject[]> {
    let remoteUrl = `${kBaseUrl}list/${module}`;

    let body = new URLSearchParams();

    return this.http.post<LooseObject[]>(remoteUrl, body.toString());
  }

  listShownInDashboard(term: string = '', page: number = 1): Observable<LooseObject[]> {
    let remoteUrl = `${kBaseUrl}shown_in_dashboard?search_term=${term}&page=${page}`;
    let body = new URLSearchParams();
    return this.http.post<LooseObject[]>(remoteUrl, body.toString());
  }

  getDashboardReportRecord(id: string ): Observable<LooseObject[]> {
    let remoteUrl = `${kBaseUrl}shown_in_dashboard/${id}`;
    let body = new URLSearchParams();
    return this.http.post<LooseObject[]>(remoteUrl, body.toString());
  }

  getAdvanceReportData(config: LooseObject, module: string): Observable<AdvanceReportResponse> {
    let remoteUrl = `${kBaseUrl}get_report_data/${module}`;

    let body = new URLSearchParams();
    body.append('filters', JSON.stringify(config.filters));
    body.append('fields', JSON.stringify(config.fields));
    body.append('datapoints', JSON.stringify(config.datapoints));
    body.append('chart_config', JSON.stringify(config.chart_config));
    if (config.sort !== undefined && isObject(config.sort) && filled(config.sort)) {
      body.append('sort', JSON.stringify(config.sort));
    }
    if (config.page !== undefined && isNumber(config.page) && filled(config.page)) {
      body.append('page', config.page.toString());
    }
    if (config.page_size !== undefined && isNumber(config.page_size) && filled(config.page_size)) {
      body.append('page_size', config.page_size.toString());
    }
    if (config.remove_aggregations !== undefined && isBoolean(config.remove_aggregations)) {
      body.append('remove_aggregations', config.remove_aggregations.toString());
    }
    if (config.extra_filter !== undefined && isObject(config.extra_filter) && filled(config.extra_filter)) {
      body.append('extra_filter', JSON.stringify(config.extra_filter));
    }
    if (filled(config.asset_type_id)) {
      body.append('asset_type_id', config.asset_type_id);
    }

    return this.http.post<AdvanceReportResponse>(remoteUrl, body.toString());
  }

  saveAdvanceReportData(config: LooseObject, module: string, recordId: string = null): Observable<LooseObject[]> {
    let remoteUrl = `${kBaseUrl}save`;

    let body = new URLSearchParams();
    body.append('name', config.name);
    body.append('module', module);
    body.append('filters', JSON.stringify(config.filters));
    body.append('fields', JSON.stringify(config.fields));
    body.append('field_aliases', JSON.stringify(config.field_aliases));
    body.append('datapoints', JSON.stringify(config.datapoints));
    body.append('chart_config', JSON.stringify(config.chart_config));
    if (filled(config.asset_type_id)) {
      body.append('asset_type_id', config.asset_type_id);
    }
    body.append('show_in_dashboard', config.show_in_dashboard);

    let data = body.toString();

    if (recordId) {
      return this.http.put<LooseObject[]>(`${remoteUrl}/${recordId}`, data);
    } else {
      return this.http.post<LooseObject[]>(remoteUrl, data);
    }
  }

  aggregationResultToChart(aggregation: Aggregation, chartConfig: ChartConfig) {
    const buckets = aggregation.buckets;
    const grouping = chartConfig['grouping_config'];
    let series = [];
    chartConfig.datasets.forEach((dataset, index) => {
      // Stacked chart for this kind of dataset
      if (dataset.type === 'field') {
        let tempSeries = {};
        const hasLegends =
          chartConfig.grouping_type === 'terms' &&
          filled(data_get(aggregation['legends'],chartConfig.terms.field));

        // Setting up all series (Number of unique field value)
        for (const bucket of buckets) {
          const fieldBucket = data_get(bucket, dataset.name);
          if (blank(fieldBucket)) continue;
          for (const subBucket of fieldBucket.buckets) {
            const key = `${dataset.name}-${subBucket.key}`;
            if (blank(tempSeries[key])) {
              tempSeries[key] = {
                name: `${dataset.name} - ${this.translate.instant(subBucket.key)}`,
                type: dataset.chart_type || 'bar',
                data: [],
                bucketName: dataset.name,
                subBucketName: subBucket.key,
                stack: dataset.name,
                symbol: 'circle',
                symbolSize: 15,
                fieldName: dataset.field,
              };

              if (hasLegends) {
                const fieldConfig = aggregation['legends'][dataset.field].find(x => x.name === subBucket.key);
                if (fieldConfig) {
                  tempSeries[key].color = fieldConfig.color || 'white';
                }
              }
            }
          }
        }

        // Setting up data count for each unique field per group type (Must set to 0 if not found since data count per series should be uniform)
        Object.keys(tempSeries).forEach(seriesName => {
          const subBucketName = tempSeries[seriesName].subBucketName;

          for (const bucket of buckets) {
            const fieldBucket = data_get(bucket, dataset.name);
            if (blank(fieldBucket)) continue;
            const isFound = fieldBucket.buckets.find(subBucket => subBucket.key === subBucketName);
            tempSeries[seriesName].data.push({
              value: isFound ? isFound.doc_count : 0,
              itemStyle: {
                borderColor: null,
                borderWidth: null,
                borderRadius: null,
                borderType: 'solid',
              },
            })
          }
        });

        series = series.concat(Object.values(tempSeries));
      } else {
        let datasetConfig = {
          name: dataset.name,
          type: dataset.chart_type || 'bar',
          data: buckets.map((bucket) => {

            if (bucket[dataset.name].aggregated_result === undefined) {
              // Drilldown chart (When using mutli bucket as dataset)
              return {
                value: bucket.doc_count,
                itemStyle: {},
                drilldownData: this.setupDrilldownData(bucket.key_as_string, dataset.actual_field, bucket[dataset.name]),
              };
            } else {
              // Regular chart
              const datasetConf = {
                value: bucket[dataset.name].aggregated_result.value || 0,
                itemStyle: {},
              };
              // Will use colors defined from the dropdown config instead of chart color
              if (chartConfig.grouping_type === 'terms' && aggregation['legends'][chartConfig.terms.field] !== undefined) {
                const fieldConfig = aggregation['legends'][chartConfig.terms.field].find(x => x.name === bucket.key);
                if (fieldConfig) {
                  datasetConf.itemStyle = {
                    color: fieldConfig.color || 'white',
                    borderColor: dataset.color,
                    borderWidth: '4',
                    borderRadius: '10',
                    borderType: 'solid'
                  };
                }
              }
              return datasetConf;
            }
          }),
          color: dataset.color,
          symbol: 'circle',
          symbolSize: 15,
          yAxisIndex: index,
        }
        series.push(datasetConfig);
      }
    });
    let toolboxConfig = {
      show: true,
      feature: {
        saveAsImage: {
          show: true,
        },
        magicType: {
          show: true,
          type: ['line', 'bar'],
        },
        restore: {
          show: true,
        },
        dataView: {
          show: true,
          readOnly: true,
        },
      }
    };

    const chartOptions = {
      title: {
        text: chartConfig.name,
      },
      dataZoom: [
        {
          type: 'slider',
          labelFormatter: (value, label) => transform(label, {
            default_value: '',
            transformer: (label) => this.translate.instant(label)
          }),
        }
      ],
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
        formatter: (params) => {
          let tooltipContent = '<div style="font-size: 12px;">';
          tooltipContent += spf('<b>%s: %s</b>', {
            args: [
              this.translate.instant(fallback(data_get(grouping, 'label'), {
                fallback: () => data_get(grouping, 'field'),
              })),
              this.translate.instant(params[0].name),
            ],
          });
          // Iterate over the params array to construct the tooltip content
          params.forEach((item) => {
            if (item.value > 0) {
              // Construct HTML for colored legend with inline styles
              tooltipContent += '<div><span style="display: inline-block; width: 12px; height: 12px; margin-right: 5px; background-color: ' + item.color + '; vertical-align: middle;"></span>' +
                item.seriesName + ': ' + item.value + '</div>';
            }
          });

          tooltipContent += '</div>';
          return tooltipContent;
        }
      },
      legend: {
        data: series.map(x => x.name),
        textStyle: {
          color: '#333'
        },
        top: 30,
        type: 'scroll',
      },
      toolbox: merge(toolboxConfig, this.translate.instant("ECHARTS.toolbox")),
      yAxis: series.map((ser, index) => ({
        type: 'value',
        name: '', // Hide yAxis label
        nameTextStyle: {
          fontSize: 12,
          fontWeight: 'bold',
        },
        position: index % 2 === 0 ? 'left' : 'right',
        axisLine: {
          lineStyle: {
            color: ser.color,
            width: 2,
          }
        },
        axisLabel: {
          show: true,
          color: '#333',
          fontSize: 12,
        }
      })),
      xAxis: {
        type: 'category',
        data: buckets.map((x: LooseObject) => x.key_as_string || x.key),
        axisLabel: {
          formatter: (value: string) => this.translate.instant(value),
        }
      },
      series: series,
      // This is not a native echarts chart option and will only be used specifically for terms grouping type
      categoryMapping: buckets.map((x: LooseObject): CategoryMapping => {
        // ES Dropdown single value structure
        let dropdownValue = {
          label: x.key_as_string || x.key,
          value: x.key_as_string || x.key,
        };

        // ES Relate single value structure
        let relateValue = {
          id: x.key,
          name: x.key_as_string || x.key,
          raw: {
            id: x.key,
            name: x.key_as_string || x.key,
          }
        }

        return {
          label: x.key_as_string || x.key,
          value: filled(aggregation.is_relate) && aggregation.is_relate ? relateValue : dropdownValue,
        }
      }),
    };

    return chartOptions;
  }

  setupDrilldownData(name: string, field: string, aggregation: Aggregation) {
    let chartOption = {};
    const buckets = aggregation.buckets;
    const commonChartOption = {
      title: {
        text: this.translate.instant(name) + ' - ' + this.translate.instant(field),
        show: false,
        textStyle: {
          fontWeight: 'normal',
          fontSize: 16,
          color: '#333',
        },
        subtextStyle: {
          fontWeight: 'normal',
          fontSize: 12,
          color: '#333',
        }
      },
      tooltip: {
        trigger: 'item',
      },
      legend: {
        type: 'scroll',
        orient: 'horizontal',
        bottom: 0,
        left: 'center',
        pageIconColor: '#000',
        pageTextStyle: {
          color: '#000'
        },
      },
      graphic: [
        {
          type: 'text',
          top: 5,
          left: 5,
          style: {
            text: '<- Back',
            fontSize: 18,
            fontWeight: 'bold',
            fill: '#007BFF',
            cursor: 'pointer'
          },
        }
      ],
    };

    const axisValue = {
      type: 'value'
    };
    const axisCategory = {
      type: 'category',
      data: [
        this.translate.instant(field)
      ],
      axisLabel: {
        formatter: (value: string) => this.translate.instant(value),
      }
    };

    chartOption = {
      ...commonChartOption,
      xAxis: axisCategory,
      yAxis: axisValue,
      series: buckets.map((item) => ({
        name: this.translate.instant(item.key_as_string || item.key),
        type: 'bar',
        data: [round(parseFloat(item.aggregated_result.value), 2)]
      })),
    }

    return chartOption;
  }

  getDashboardData(config: LooseObject, dashboard: DashboardType, module: string): Observable<DashboardDataResponse> {
    let remoteUrl = `${kBaseUrl}dashboard_data/${dashboard}/${module}`;

    let body = new URLSearchParams();
    body.append('filters', JSON.stringify(config.filters));

    return this.http.post<AdvanceReportResponse>(remoteUrl, body.toString());
  }
  /**
   * Get module metadata with directly related fields
   *
   * @return {Observable<AdvanceSearchboxTemplate[]>}
   */
  getModuleMetadata(strModule: string): Observable<AdvanceSearchboxTemplate[]> {
    const metadataUrl = `${kBaseUrl}module_metadata/${strModule}`;
    let body = new URLSearchParams();
    // Get metadata from the server
    return this.http.post<AdvanceSearchboxTemplate[]>(metadataUrl, body.toString()).pipe(
      map((template) => {
        // Translate labels
        return template.map((t: AdvanceSearchboxTemplate) => {
          // For date/datetime filter
          const dateFilters = ['date', 'datetime'];
          // Update format based on locale
          if (dateFilters.includes(t.dataType)) {
            // Change locale: chrome://settings/languages
            // Date validation
            t.mask = {
              mask: t.placeholder
                // .replace(/\//g, '-')
                .replace('DD', 'd0')
                .replace('MM', 'M0')
                .replace('YYYY', '0000'),
              clearIfNotMatch: true,
              dropSpecialCharacters: false,
            };
          }
          return t;
        });
      })
    );
  }
}
